/**
 * Phase 5 batch 3 — Firebase-backed wrapper for the intake App.
 *
 * Initializes the Firebase SDK once, builds the four adapter clients
 * (auth, persistence, storage, audit writer), and renders the
 * AuthGate. When a verified user is present we call
 * `linkUserToApplication` to resolve the user's `defaultApplicationId`
 * (creating both the user doc and a fresh application doc if this is
 * the first sign-in), then mount `<App />` with the Firebase-backed
 * dependency overrides:
 *
 *   - `persistenceAdapter`  → Firestore-backed adapter (Slice 3)
 *   - `uploadDocument`      → Cloud Storage upload + Firestore metadata (Slice 4)
 *   - `connectAudit`        → Firestore audit writer (Slice 5)
 *   - `topBarSlot`          → SignedInBadge + Privacy link
 *   - `applicationId`       → user's resolved app id
 *   - cross-tab sync via the adapter's `subscribe` (Slice 6)
 *
 * Tests do NOT render this component — they render `<App />` directly
 * with stub deps. The Firebase SDK never boots in vitest.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  type AuthClient,
  createAuthClient,
  createFirestoreAuditWriter,
  createFirestorePersistenceAdapter,
  createPacketDownloadClient,
  createStorageClient,
  linkUserToApplication,
  submitApplication,
  type FirestorePersistenceAdapter,
  type StorageClient,
} from '@inside-us-aos/firebase-adapter';
import type { FirebaseApp as IFirebaseApp } from 'firebase/app';
import {
  AOS_V1_SCHEMA_VERSION,
  type FieldId,
} from '@inside-us-aos/domain';
import type { IUploadedFileMetadata } from '@inside-us-aos/ui';
import {
  type Firestore,
  doc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import type { User } from 'firebase/auth';

import { App, type IAppProps } from '../App';
import { AuthGate } from '../auth/AuthGate';
import { SignedInBadge } from '../auth/SignedInBadge';
import { useTesterClaim } from '../auth/useTesterClaim';
import { reportBackgroundWriteFailure } from './backgroundWriteReporting';
import { tryGetFirebaseRuntime } from './init';
import { PrivacyPage } from './PrivacyPage';
import { App as DemoApp } from '../App';

interface IResolvedApplication {
  applicationId: string;
}

export function FirebaseApp(): JSX.Element {
  const runtimeResult = useMemo(() => tryGetFirebaseRuntime(), []);
  const [showPrivacy, setShowPrivacy] = useState(false);

  if (showPrivacy) {
    return <PrivacyPage onClose={() => setShowPrivacy(false)} />;
  }

  // Demo-mode fallback: when Firebase env vars aren't configured
  // (typical fresh checkout before .env.local is filled in), render
  // the App with its built-in localStorage-backed defaults instead of
  // crashing with a blank page. Production deploys MUST have the env
  // vars set; the banner makes the misconfiguration impossible to
  // miss.
  if (!runtimeResult.ok) {
    return (
      <>
        <div
          role="alert"
          style={{
            background: '#fef3c7',
            borderBottom: '1px solid #f59e0b',
            color: '#78350f',
            padding: '12px 16px',
            fontFamily:
              'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
            fontSize: '13px',
            lineHeight: 1.5,
          }}
        >
          <strong>Firebase not configured.</strong> Running in demo mode
          with localStorage. Auth, cross-tab sync, document upload, and
          the audit log are disabled until you copy{' '}
          <code>apps/applicant-web/.env.example</code> to{' '}
          <code>apps/applicant-web/.env.local</code> and fill in the
          values from the Firebase Console. Details:{' '}
          <code>{runtimeResult.error.message.split('\n')[0]}</code>
        </div>
        <DemoApp />
      </>
    );
  }

  const runtime = runtimeResult.runtime;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const authClient = createAuthClient(runtime.auth, {
    // After the user clicks the verification link, Firebase appends
    // this URL as the `continueUrl` query param. Our custom handler
    // (`/verify-email`) reads it and redirects on success, so the
    // user lands in our intake instead of Firebase's hosted page.
    verifyEmailContinueUrl:
      typeof window !== 'undefined'
        ? `${window.location.origin}/intake`
        : undefined,
  });
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const storageClient = createStorageClient(runtime.storage);

  return (
    <AuthGate
      authClient={authClient}
      onShowPrivacy={() => setShowPrivacy(true)}
    >
      {(user) => (
        <ResolveAndMount
          authClient={authClient}
          firestore={runtime.firestore}
          firebaseApp={runtime.app}
          storageClient={storageClient}
          user={user}
          showPrivacy={() => setShowPrivacy(true)}
        />
      )}
    </AuthGate>
  );
}

function ResolveAndMount({
  authClient,
  firestore,
  firebaseApp,
  storageClient,
  user,
  showPrivacy,
}: {
  authClient: AuthClient;
  firestore: Firestore;
  firebaseApp: IFirebaseApp;
  storageClient: StorageClient;
  user: User;
  showPrivacy: () => void;
}): JSX.Element {
  const [resolved, setResolved] = useState<IResolvedApplication | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // When the user lands on the app after clicking the
    // email-verification link, the cached ID token still carries
    // `email_verified: false` (the token was minted at signup, before
    // verification). Firestore rules require `email_verified == true`
    // (see `isVerified()` in firestore.rules), so the very first
    // hydrate after verification fails with "Missing or insufficient
    // permissions" until the user manually signs out + in to mint a
    // fresh token.
    //
    // Force a token refresh before linking — `getIdToken(true)` calls
    // user.reload() and re-fetches a fresh token with the up-to-date
    // claims. Idempotent + cheap on subsequent loads (Firebase caches
    // the result for ~1 hour).
    void user
      .reload()
      .then(() => user.getIdToken(true))
      .then(() =>
        linkUserToApplication(
          firestore,
          {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
          },
          { schemaVersion: AOS_V1_SCHEMA_VERSION },
        ),
      )
      .then((linkage) => {
        if (cancelled) return;
        setResolved({ applicationId: linkage.applicationId });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : 'Could not initialize application data.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [firestore, user.uid, user.email, user.displayName]);

  if (error) {
    return (
      <div style={{ padding: '2rem', maxWidth: '32rem', margin: '4rem auto' }}>
        <h1>We could not load your application</h1>
        <p>{error}</p>
        <button type="button" onClick={() => void authClient.signOut()}>
          Sign out and try again
        </button>
      </div>
    );
  }

  if (!resolved) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#54627a' }}>
        Setting up your application…
      </div>
    );
  }

  return (
    <FirebaseIntake
      authClient={authClient}
      firestore={firestore}
      firebaseApp={firebaseApp}
      storageClient={storageClient}
      user={user}
      applicationId={resolved.applicationId}
      showPrivacy={showPrivacy}
    />
  );
}

function FirebaseIntake({
  authClient,
  firestore,
  firebaseApp,
  storageClient,
  user,
  applicationId,
  showPrivacy,
}: {
  authClient: AuthClient;
  firestore: Firestore;
  firebaseApp: IFirebaseApp;
  storageClient: StorageClient;
  user: User;
  applicationId: string;
  showPrivacy: () => void;
}): JSX.Element {
  const persistenceAdapter: FirestorePersistenceAdapter = useMemo(
    () => createFirestorePersistenceAdapter(firestore),
    [firestore],
  );

  const auditWriter = useMemo(
    () =>
      createFirestoreAuditWriter(firestore, {
        actorUserId: user.uid,
        onError: (err) => {
          // Audit flush failures are user-invisible (the audit batch
          // sits in memory + retries on the next nav-boundary flush).
          // Transient connectivity and post-offline permission blips
          // self-heal on retry and ride along as breadcrumbs; only a
          // genuinely unexpected failure pages as an error-level issue.
          // See backgroundWriteReporting.ts (Sentry JS-REACT-D triage).
          reportBackgroundWriteFailure(err, { kind: 'audit', flow: 'flush' });
        },
      }),
    [firestore, user.uid],
  );

  const connectAudit = useMemo<NonNullable<IAppProps['connectAudit']>>(() => {
    return (source) => auditWriter.connect(source, applicationId);
  }, [auditWriter, applicationId]);

  const uploadDocument = useMemo<
    NonNullable<IAppProps['uploadDocument']>
  >(() => {
    return async (
      fieldId: FieldId,
      file: IUploadedFileMetadata,
    ): Promise<{ uploadedFileId: string }> => {
      // The DocumentUpload primitive carries IUploadedFileMetadata
      // (name/type/size). The raw File object is attached on a
      // private property by the Firebase wrapper's onChange shim.
      const rawFile =
        (file as IUploadedFileMetadata & { __file?: File }).__file ??
        new Blob([new Uint8Array()], {
          type: file.type ?? 'application/octet-stream',
        });
      const uploaded = await storageClient.uploadDocument(
        { uid: user.uid },
        applicationId,
        fieldId,
        rawFile,
      );
      await setDoc(
        doc(
          firestore,
          'applications',
          applicationId,
          'documents',
          uploaded.fileId,
        ),
        {
          fieldId,
          storagePath: uploaded.storagePath,
          fileName: file.name,
          contentType: uploaded.contentType,
          sizeBytes: uploaded.size,
          uploadedAt: serverTimestamp(),
          uploadedByUserId: user.uid,
        },
      );
      return { uploadedFileId: uploaded.fileId };
    };
  }, [storageClient, firestore, applicationId, user.uid]);

  /**
   * Phase 8 wiring — Submit-for-review click writes
   * `caseState: 'in_review'` + `submittedAt` on the application doc
   * and stamps a `caseStateChanges` audit row. The post-submit
   * waiting UX (commit 985026d) then takes over because App.tsx
   * routes on caseState.
   */
  const handleSubmitForReview = useMemo(
    () => async (): Promise<void> => {
      await submitApplication(firestore, applicationId, {
        actorUid: user.uid,
      });
    },
    [firestore, applicationId, user.uid],
  );

  /**
   * Packet-download wiring — calls the `getPacketDownloadUrl` Cloud
   * Function, which mints a 1-hour V4 signed URL and authorizes the
   * caller against the application's `ownerUserId` (or the admin
   * claim). The URL is opened in a new tab and never persisted.
   *
   * Callable client is built once per application id; tests don't
   * render this component (they render `<App />` directly), so the
   * indirection is safe.
   */
  const handleDownloadPacket = useMemo(() => {
    const callPacketDownloadUrl = createPacketDownloadClient(firebaseApp);
    return async (): Promise<void> => {
      const { url } = await callPacketDownloadUrl(applicationId);
      if (typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener');
      }
    };
  }, [firebaseApp, applicationId]);

  // Paywall gate for the in-browser Download buttons. Defaults to
  // false; only flips when the user's ID token carries the
  // `tester: true` custom claim. Paying customers never have the
  // claim and therefore never see the Download UI.
  const isTester = useTesterClaim(user);

  return (
    <App
      applicationId={applicationId}
      persistenceAdapter={persistenceAdapter}
      uploadDocument={uploadDocument}
      connectAudit={connectAudit}
      updatedBy={user.uid}
      onSubmitForReview={handleSubmitForReview}
      onDownloadPacket={handleDownloadPacket}
      isTester={isTester}
      topBarSlot={
        <span style={{ display: 'inline-flex', gap: '0.75rem' }}>
          <SignedInBadge authClient={authClient} user={user} />
          <button
            type="button"
            onClick={showPrivacy}
            style={{
              background: 'none',
              border: 0,
              color: '#2563eb',
              cursor: 'pointer',
              fontSize: '0.78rem',
              padding: 0,
            }}
          >
            Privacy
          </button>
        </span>
      }
    />
  );
}
